/**
 * 波形上のドラッグ範囲選択。
 *
 * 波形本体のドラッグで in/out を引き、両端のハンドルで後から詰められる。
 * 閾値未満の移動はクリック扱いにして、選択範囲の外ならシーク + 選択解除、
 * 内側ならシークのみ（範囲を保ったまま頭出しし直せる）とする。
 */

import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';

export interface RangeSelection {
  start: number;
  end: number;
}

/** これ未満のドラッグはクリックとみなす */
const CLICK_THRESHOLD_PX = 3;

/** これ未満の範囲は選択として扱わない（誤操作の極小範囲を弾く） */
const MIN_SELECTION_SECONDS = 0.01;

type DragKind = 'create' | 'start' | 'end';

export interface UseRangeSelectionOptions {
  /** 波形全体の尺（秒）。0 のあいだは操作を受け付けない */
  duration: number;
  /** クリック / ドラッグ開始時のシーク */
  onSeek?: (_seconds: number) => void;
  /** 選択が確定・解除されたとき（ドラッグ終了時のみ発火） */
  onCommit?: (_selection: RangeSelection | null) => void;
}

export interface UseRangeSelectionResult {
  containerRef: RefObject<HTMLDivElement | null>;
  selection: RangeSelection | null;
  /** 波形本体に張る pointerdown ハンドラ */
  onPointerDown: (_event: ReactPointerEvent) => void;
  /** in / out ハンドルに張る pointerdown ハンドラを作る */
  makeHandlePointerDown: (
    _edge: 'start' | 'end',
  ) => (_event: ReactPointerEvent) => void;
  /** 選択を解除する（別ファイルへ切り替えたときは呼び出し側が呼ぶ） */
  clear: () => void;
  /** 選択範囲をプログラムから設定する（範囲ブックマークの復元用） */
  set: (_range: RangeSelection) => void;
}

export function useRangeSelection({
  duration,
  onSeek,
  onCommit,
}: UseRangeSelectionOptions): UseRangeSelectionResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selection, setSelection] = useState<RangeSelection | null>(null);

  const durationRef = useRef(duration);
  durationRef.current = duration;
  const onSeekRef = useRef(onSeek);
  onSeekRef.current = onSeek;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const timeAtClientX = useCallback((clientX: number): number => {
    const element = containerRef.current;
    const total = durationRef.current;
    if (!element || total <= 0) return 0;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(total, ratio * total));
  }, []);

  const beginDrag = useCallback(
    (event: ReactPointerEvent, kind: DragKind) => {
      if (durationRef.current <= 0 || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      const originX = event.clientX;
      const pressedTime = timeAtClientX(originX);
      // ハンドル操作では掴んでいない側の端が固定点になる
      const anchor =
        kind === 'start'
          ? (selection?.end ?? pressedTime)
          : kind === 'end'
            ? (selection?.start ?? pressedTime)
            : pressedTime;
      // 新規ドラッグは閾値を超えるまでクリック扱いにして、既存の選択を残す。
      // 「範囲内クリック = 範囲を保ったまま頭出し」を成立させるため
      let moved = kind !== 'create';

      const rangeTo = (clientX: number): RangeSelection => {
        const current = timeAtClientX(clientX);
        return { start: Math.min(anchor, current), end: Math.max(anchor, current) };
      };
      const isValid = (range: RangeSelection) =>
        range.end - range.start >= MIN_SELECTION_SECONDS;

      const handleMove = (moveEvent: PointerEvent) => {
        if (!moved && Math.abs(moveEvent.clientX - originX) < CLICK_THRESHOLD_PX) return;
        moved = true;
        const next = rangeTo(moveEvent.clientX);
        setSelection(isValid(next) ? next : null);
      };

      const handleUp = (upEvent: PointerEvent) => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);

        if (!moved) {
          // クリック: 範囲の外ならリセット、内側なら範囲を保ったまま頭出し
          const clickedTime = timeAtClientX(upEvent.clientX);
          if (selection && (clickedTime < selection.start || clickedTime > selection.end)) {
            setSelection(null);
            onCommitRef.current?.(null);
          }
          onSeekRef.current?.(clickedTime);
          return;
        }

        const committed = rangeTo(upEvent.clientX);
        const valid = isValid(committed);
        setSelection(valid ? committed : null);
        onCommitRef.current?.(valid ? committed : null);
        if (valid) onSeekRef.current?.(committed.start);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [selection, timeAtClientX],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => beginDrag(event, 'create'),
    [beginDrag],
  );

  const makeHandlePointerDown = useCallback(
    (edge: 'start' | 'end') => (event: ReactPointerEvent) => beginDrag(event, edge),
    [beginDrag],
  );

  const clear = useCallback(() => {
    setSelection(null);
    onCommitRef.current?.(null);
  }, []);

  const set = useCallback((range: RangeSelection) => {
    setSelection(range);
    onCommitRef.current?.(range);
  }, []);

  return {
    containerRef,
    selection,
    onPointerDown,
    makeHandlePointerDown,
    clear,
    set,
  };
}
